import { useState, useEffect } from "react";
import axios from "axios";
import { API, USER_ID } from "../App";
import { 
  Plus, 
  Trash,
  Barbell,
  Heartbeat,
  PersonSimpleRun,
  Lightning,
  Timer,
  Fire,
  X
} from "@phosphor-icons/react";
import { toast } from "sonner";

const WORKOUT_TYPES = [
  { id: "strength", label: "Musculation", icon: Barbell, color: "bg-red-500" },
  { id: "cardio", label: "Cardio", icon: Heartbeat, color: "bg-blue-500" },
  { id: "hiit", label: "HIIT", icon: Lightning, color: "bg-orange-500" },
  { id: "flexibility", label: "Flexibilité", icon: PersonSimpleRun, color: "bg-purple-500" },
];

const EXERCISE_PRESETS = {
  strength: [
    { name: "Développé couché", sets: 4, reps: 10 },
    { name: "Squat", sets: 4, reps: 8 },
    { name: "Soulevé de terre", sets: 3, reps: 8 },
    { name: "Rowing barre", sets: 4, reps: 10 },
    { name: "Développé épaules", sets: 3, reps: 12 },
    { name: "Curl biceps", sets: 3, reps: 12 },
  ],
  cardio: [
    { name: "Course à pied", duration: 30 },
    { name: "Vélo", duration: 45 },
    { name: "Rameur", duration: 20 },
    { name: "Natation", duration: 30 },
  ],
  hiit: [
    { name: "Burpees", sets: 4, reps: 15 },
    { name: "Mountain climbers", sets: 4, reps: 30 },
    { name: "Jump squats", sets: 4, reps: 20 },
  ],
  flexibility: [
    { name: "Yoga flow", duration: 30 },
    { name: "Étirements", duration: 15 },
    { name: "Foam rolling", duration: 20 },
  ],
};

const Workouts = ({ profile }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [workouts, setWorkouts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workoutType, setWorkoutType] = useState("strength");
  const [workoutName, setWorkoutName] = useState("");
  const [duration, setDuration] = useState(60);
  const [caloriesBurned, setCaloriesBurned] = useState(300);
  const [exercises, setExercises] = useState([]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchWorkouts();
  }, [selectedDate]);

  const fetchWorkouts = async () => {
    try {
      const response = await axios.get(`${API}/workouts/${USER_ID}?date=${selectedDate}`);
      setWorkouts(response.data);
    } catch (error) {
      console.error("Error fetching workouts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExercise = (exercise) => {
    setExercises([...exercises, { ...exercise, id: Date.now() }]);
  };

  const handleRemoveExercise = (id) => {
    setExercises(exercises.filter(e => e.id !== id));
  };

  const handleCreateWorkout = async () => {
    if (!workoutName.trim()) {
      toast.error("Nom de séance requis");
      return;
    }
    try {
      await axios.post(`${API}/workouts`, {
        user_id: USER_ID,
        date: selectedDate,
        workout_type: workoutType,
        name: workoutName,
        duration_minutes: duration,
        calories_burned: caloriesBurned,
        exercises: exercises,
        notes: notes,
      });
      toast.success("Séance ajoutée !");
      setShowAddModal(false);
      resetForm();
      fetchWorkouts();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    try {
      await axios.delete(`${API}/workouts/${workoutId}`);
      toast.success("Séance supprimée");
      fetchWorkouts();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setWorkoutType("strength");
    setWorkoutName("");
    setDuration(60);
    setCaloriesBurned(300);
    setExercises([]);
    setNotes("");
  };

  const totalDuration = workouts.reduce((sum, w) => sum + w.duration_minutes, 0);
  const totalCalories = workouts.reduce((sum, w) => sum + w.calories_burned, 0);

  return (
    <div className="p-4 md:p-8 animate-fadeIn max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl uppercase tracking-tight text-charcoal">Entraînements</h1>
        <p className="text-gray-500">Programme tes séances, garde le rythme et note ce que tu fais vraiment.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-4 py-3 rounded-xl border border-gray-200 bg-white text-charcoal"
          data-testid="workout-date-selector"
        />
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center justify-center gap-2"
          data-testid="add-workout-button"
        >
          <Plus size={20} weight="bold" />
          Nouvelle séance
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card p-4 text-center">
          <Barbell size={24} className="text-gold mx-auto mb-2" />
          <p className="text-2xl font-bold text-charcoal">{workouts.length}</p>
          <p className="text-xs text-gray-500">Séances</p>
        </div>
        <div className="card p-4 text-center">
          <Timer size={24} className="text-gold mx-auto mb-2" />
          <p className="text-2xl font-bold text-charcoal">{totalDuration}</p>
          <p className="text-xs text-gray-500">Minutes</p>
        </div>
        <div className="card p-4 text-center">
          <Fire size={24} className="text-gold mx-auto mb-2" />
          <p className="text-2xl font-bold text-gold">{totalCalories}</p>
          <p className="text-xs text-gray-500">Kcal</p>
        </div>
      </div>

      {/* Workouts List */}
      <div className="space-y-4">
        {workouts.length === 0 ? (
          <div className="card p-8 text-center">
            <Barbell size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Aucun entraînement</p>
            <button onClick={() => setShowAddModal(true)} className="btn-primary" data-testid="add-first-workout">
              Ajouter une séance
            </button>
          </div>
        ) : (
          workouts.map((workout) => {
            const typeInfo = WORKOUT_TYPES.find(t => t.id === workout.workout_type) || WORKOUT_TYPES[0];
            const Icon = typeInfo.icon;
            return (
              <div key={workout.id} className="card overflow-hidden" data-testid={`workout-card-${workout.id}`}>
                <div className={`${typeInfo.color} px-5 py-4 flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <Icon size={22} weight="fill" className="text-white" />
                    <span className="font-semibold text-white">{workout.name}</span>
                  </div>
                  <span className="text-sm text-white/90">{typeInfo.label}</span>
                </div>
                <div className="p-4">
                  <div className="flex gap-6 mb-4">
                    <div className="flex items-center gap-2">
                      <Timer size={16} className="text-gray-400" />
                      <span className="text-charcoal">{workout.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Fire size={16} className="text-gold" />
                      <span className="text-gold font-bold">{workout.calories_burned} kcal</span>
                    </div>
                  </div>
                  {workout.exercises?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {workout.exercises.map((ex, idx) => (
                        <span key={idx} className="px-3 py-1 bg-cream rounded-full text-sm text-charcoal">
                          {ex.name}
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => handleDeleteWorkout(workout.id)}
                    className="text-gray-400 hover:text-red-500 text-sm flex items-center gap-1"
                    data-testid={`delete-workout-${workout.id}`}
                  >
                    <Trash size={16} /> Supprimer
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="add-workout-modal">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-display text-xl text-charcoal">Nouvelle séance</h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-2 text-gray-400 hover:text-charcoal" data-testid="close-workout-modal">
                <X size={24} />
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block font-medium">Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {WORKOUT_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setWorkoutType(type.id)}
                        className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          workoutType === type.id ? 'border-gold bg-gold-light' : 'border-gray-200 hover:border-gold'
                        }`}
                        data-testid={`workout-type-${type.id}`}
                      >
                        <Icon size={24} className={workoutType === type.id ? 'text-gold' : 'text-gray-400'} />
                        <span className="text-sm">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block font-medium">Nom</label>
                <input
                  type="text"
                  value={workoutName}
                  onChange={(e) => setWorkoutName(e.target.value)}
                  placeholder="Ex: Push day"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200"
                  data-testid="workout-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block font-medium">Durée (min)</label>
                  <input type="number" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="workout-duration-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block font-medium">Calories</label>
                  <input type="number" value={caloriesBurned} onChange={(e) => setCaloriesBurned(Number(e.target.value))} className="w-full px-4 py-3 rounded-xl border border-gray-200" data-testid="workout-calories-input" />
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block font-medium">Exercices</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {EXERCISE_PRESETS[workoutType]?.map((ex, idx) => (
                    <button key={idx} onClick={() => handleAddExercise(ex)} className="px-3 py-1 bg-cream rounded-full text-sm hover:bg-gold-light" data-testid={`add-exercise-${idx}`}>
                      + {ex.name}
                    </button>
                  ))}
                </div>
                {exercises.length > 0 && (
                  <div className="space-y-2">
                    {exercises.map((ex) => (
                      <div key={ex.id} className="flex items-center justify-between bg-cream rounded-lg p-2">
                        <span className="text-charcoal">{ex.name}</span>
                        <button onClick={() => handleRemoveExercise(ex.id)} className="text-gray-400 hover:text-red-500">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button onClick={handleCreateWorkout} className="w-full btn-primary" data-testid="confirm-add-workout">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Workouts;
